import { lazy } from 'react';
import type { RouteObject } from 'react-router';
import { Navigate, useLocation, useParams } from 'react-router';
import { RequireAuth } from './components/RequireAuth';
import { SiteShell } from './components/SiteShell';
import { HomePage } from './pages/HomePage';
import { SignInPage } from './pages/SignInPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { NotFoundPage, RouteErrorBoundary } from './pages/NotFoundPage';

// Lazy-loaded pages — split out of the home-page critical bundle. The
// Suspense boundary lives in SiteShell wrapping the Outlet, so each route
// renders a loading state until its chunk resolves. PublicSweepstakePage is
// lazy because it pulls react-markdown (~50 KiB); the admin chain is lazy
// because anonymous visitors never see those routes.
const PublicSweepstakePage = lazy(() =>
  import('./pages/PublicSweepstakePage').then((m) => ({ default: m.PublicSweepstakePage })),
);
const SubmitSuccessPage = lazy(() =>
  import('./pages/SubmitSuccessPage').then((m) => ({ default: m.SubmitSuccessPage })),
);
const AdminDashboardPage = lazy(() =>
  import('./pages/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })),
);
const CreateSweepstakePage = lazy(() =>
  import('./pages/CreateSweepstakePage').then((m) => ({ default: m.CreateSweepstakePage })),
);
const AdminSweepstakePage = lazy(() =>
  import('./pages/AdminSweepstakePage').then((m) => ({ default: m.AdminSweepstakePage })),
);
const ResolvePage = lazy(() =>
  import('./pages/ResolvePage').then((m) => ({ default: m.ResolvePage })),
);

// Redirect helpers preserve query params (matters for in-flight magic links that
// still point at the old /admin/sign-in?email=&token=... shape).
function RedirectPreservingSearch({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

function RedirectSweepstake({ tail = '' }: { tail?: string }) {
  const { id } = useParams();
  return <Navigate to={`/sweepstakes/${id ?? ''}${tail}`} replace />;
}

export const routes: RouteObject[] = [
  {
    element: <SiteShell />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/s/:slug', element: <PublicSweepstakePage /> },
      { path: '/s/:slug/submitted', element: <SubmitSuccessPage /> },

      // Organiser-facing routes (no /admin prefix — these aren't back-office).
      // Everything past /sign-in is gated by RequireAuth so an unauthenticated
      // visitor bounces to sign-in BEFORE they fill out a form, not after.
      { path: '/sign-in', element: <SignInPage /> },
      { path: '/dashboard', element: <RequireAuth><AdminDashboardPage /></RequireAuth> },
      { path: '/sweepstakes/new', element: <RequireAuth><CreateSweepstakePage /></RequireAuth> },
      { path: '/sweepstakes/:id', element: <RequireAuth><AdminSweepstakePage /></RequireAuth> },
      { path: '/sweepstakes/:id/resolve', element: <RequireAuth><ResolvePage /></RequireAuth> },

      // Legacy /admin/* redirects so in-flight magic-link emails and any
      // bookmarks from the first 24h of the app still work.
      { path: '/admin', element: <Navigate to="/dashboard" replace /> },
      { path: '/admin/sign-in', element: <RedirectPreservingSearch to="/sign-in" /> },
      { path: '/admin/sweepstakes/new', element: <Navigate to="/sweepstakes/new" replace /> },
      { path: '/admin/sweepstakes/:id', element: <RedirectSweepstake /> },
      { path: '/admin/sweepstakes/:id/resolve', element: <RedirectSweepstake tail="/resolve" /> },

      { path: '/privacy', element: <PrivacyPage /> },
      { path: '/terms', element: <TermsPage /> },
      // /company, /faq, /how-it-works, /charities, /guides/* are served server-side from
      // Content/**/*.md via ContentEndpoints — no React routes here. The footer Link to
      // /company is intentionally a plain <a> so the browser does a full load and gets
      // the server-rendered content page rather than this SPA shell's 404.

      // Catch-all 404 — anything not matched above.
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
